import { For, Show, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { Select } from '~ui/select';
import { Switch } from '~ui/switch';
import { NumberBox } from '~ui/field';
import { session, signOut } from '~/stores/auth-store';
import {
    GOAL_YIELD,
    JURISDICTIONS,
    TAX_YEAR,
    assumption,
    bracket,
    capitalRequired,
    capitalShortfall,
    goalAfterTax,
    goalProgress,
    goalShortfall,
    loading,
    profile,
    setBirthYear,
    setGoalMonthly,
    setIncome,
    setOasAnnual,
    setProvince,
    setRetired,
    setRrspRoom,
} from '~/stores/profile-store';
import { monthlyIncome } from '~/stores/portfolio-store';
import LoanTable from '~/features/loan-table';
import { goalYear, years } from '~/stores/calc-store';
import { formatCurrency, formatPercent } from '~/lib/format';
import { OAS_RECOVERY } from '~shared/tax-rates';

// Who the holder is.
//
// Everything here is an input to a calculation rather than a preference — the
// theme switch stays in the nav, and the projection assumptions stay with the
// plan they belong to. What is left is the handful of facts about a person that
// the tax figures cannot be produced without.

const Row: Component<{ label: string; children: any }> = (props) => (
    <div class='flex h-row items-stretch justify-between gap-2 border-b pl-2'>
        <div class='flex min-w-0 flex-col justify-center'>
            <Text class='truncate'>{props.label}</Text>
        </div>
        <div class='flex shrink-0 items-center'>{props.children}</div>
    </div>
);

const SectionLabel: Component<{ children: string }> = (props) => (
    <div class='flex h-row items-center border-b bg-muted px-2'>
        <Heading level={2}>{props.children}</Heading>
    </div>
);

const Profile: Component = () => (
    <Panel>
        <Toolbar>
            <ToolbarSection>
                <Heading level={2} class='pl-2'>
                    Profile
                </Heading>
            </ToolbarSection>
            <ToolbarSection>
                <Text tone='muted' class='pr-2'>
                    {loading() ? 'loading' : 'saved as you type'}
                </Text>
            </ToolbarSection>
        </Toolbar>

        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
            <div class='max-w-2xl'>
                {/* First, because it is the only thing here you actually want.
                    Everything below it is machinery for working out what the
                    goal is worth once it arrives. */}
                <SectionLabel>What you are aiming for</SectionLabel>
                <Row label='Income goal'>
                    <div class='flex items-stretch'>
                        <NumberBox value={profile.goalMonthly ?? 0} onValue={(n) => setGoalMonthly(n === 0 ? null : n)} prefix='$' width='w-36' border='l' />
                        <div class='flex items-center border-l px-2'>
                            <Text tone='muted'>/ month</Text>
                        </div>
                    </div>
                </Row>

                {/* What it takes, before where you are — it depends on the goal
                    alone, and it is the number that makes the goal concrete.
                    The assumption is in the label rather than in a footnote: a
                    capital figure with no yield attached is meaningless, and the
                    reader cannot tell which one was used. */}
                <Show when={capitalRequired() != null}>
                    <Row label={`Capital required · at ${GOAL_YIELD}% yield`}>
                        <Text weight='medium' class='px-2'>
                            {formatCurrency(capitalRequired())}
                        </Text>
                    </Row>
                    <Show when={capitalShortfall() != null && capitalShortfall()! > 0}>
                        <Row label='Still to invest'>
                            <Text class='px-2'>{formatCurrency(capitalShortfall())}</Text>
                        </Row>
                    </Show>
                </Show>

                {/* Progress, right under the box you type it into. A goal on a
                    settings page with no answer beside it is a wish; the whole
                    reason to store the number is that everything else the app
                    computes becomes a fraction of it. */}
                <Show when={goalProgress() != null}>
                    <Row label='Where you are'>
                        <Text weight='medium' tone={goalShortfall() === 0 ? 'green' : undefined} class='px-2'>
                            {formatCurrency(monthlyIncome())} · {formatPercent(goalProgress(), 1)}
                        </Text>
                    </Row>
                    <Show when={goalShortfall()! > 0}>
                        <Row label='Still to find'>
                            <Text class='px-2'>{formatCurrency(goalShortfall())} / month</Text>
                        </Row>
                    </Show>
                    {/* Only when there is a year to name. "Not within 10 years"
                        is a true sentence that does nothing for anybody — it
                        answers a question about a horizon nobody holds a goal in,
                        and it is the one line here that could put someone off
                        the whole page. Silence is the better answer until the
                        plan has something to say.

                        Under the Future tab's own assumptions, so it moves when
                        you change them there. */}
                    <Show when={goalYear() != null}>
                        <Row label='On plan'>
                            <Text class='px-2'>{goalYear() === 1 ? 'Within a year' : `About ${goalYear()} years`}</Text>
                        </Row>
                    </Show>
                </Show>

                {/* Gross, matching every "per month" figure in the app. What it
                    is worth to spend is a view of the same goal rather than a
                    second one to keep in step — and it is a large gap for this
                    pool, which is the reason to say it at all. */}
                <Show when={goalAfterTax() != null}>
                    <Row label='After tax'>
                        <Text tone='muted' class='px-2'>
                            about {formatCurrency(goalAfterTax())} / month
                        </Text>
                    </Row>
                </Show>

                <SectionLabel>Where you are</SectionLabel>
                <Row label='Province'>
                    <Select
                        class='w-56'
                        options={JURISDICTIONS.map((j) => ({ value: j.code, label: j.name }))}
                        value={profile.province ?? ''}
                        placeholder='Not set'
                        onChange={(e) => setProvince(e.currentTarget.value || null)}
                    />
                </Row>

                <Row label='Taxable income'>
                    <NumberBox value={profile.income ?? 0} onValue={(n) => setIncome(n === 0 ? null : n)} prefix='$' width='w-36' border='l' />
                </Row>

                <SectionLabel>{`Your marginal rates, ${TAX_YEAR}`}</SectionLabel>
                {/* Read-only, and derived from nothing: these are transcribed
                    published figures picked out of shared/tax-rates by province
                    and income. The gross-up and the capital gains inclusion are
                    already inside them, which is why they apply to the amount
                    actually received rather than to a grossed-up one. */}
                <Show
                    when={bracket()}
                    fallback={
                        <div class='border-b px-2 py-3'>
                            <Text tone='muted'>Set a province and an income to see them.</Text>
                        </div>
                    }>
                    <Row label='Eligible dividends'>
                        <Text weight='medium' class='px-2'>
                            {formatPercent(bracket()!.eligible, 2)}
                        </Text>
                    </Row>
                    <Row label='Capital gains'>
                        <Text weight='medium' class='px-2'>
                            {formatPercent(bracket()!.capitalGains, 2)}
                        </Text>
                    </Row>
                    <Row label='Interest and foreign income'>
                        <Text weight='medium' class='px-2'>
                            {formatPercent(bracket()!.other, 2)}
                        </Text>
                    </Row>
                    <Row label='Non-eligible dividends'>
                        <Text weight='medium' class='px-2'>
                            {formatPercent(bracket()!.nonEligible, 2)}
                        </Text>
                    </Row>
                </Show>

                <SectionLabel>Contribution room</SectionLabel>
                {/* Taken, not computed. Room is 18% of the prior year's *earned*
                    income — which excludes every dollar this app tracks — capped
                    annually, less pension adjustments only an employer and CRA
                    know. CRA prints the answer on a form everyone already has. */}
                <Row label='RRSP deduction limit'>
                    <NumberBox value={profile.rrspRoom ?? 0} onValue={(n) => setRrspRoom(n === 0 ? null : n)} prefix='$' width='w-36' border='l' />
                </Row>
                <Row label='Year of birth'>
                    <NumberBox value={profile.birthYear ?? 0} onValue={(n) => setBirthYear(n === 0 ? null : n)} width='w-36' border='l' />
                </Row>

                <SectionLabel>Retirement</SectionLabel>
                {/* A flag rather than something inferred from the year: drawing
                    OAS is a decision, not a birthday. Gated, and the gate is the
                    point — most people using this are still working, and nothing
                    below appears anywhere in the app until this is on. */}
                <Row label='Drawing OAS'>
                    <div class='flex w-36 items-center justify-end px-2'>
                        <Switch checked={profile.retired} onChange={setRetired} label='Drawing OAS' />
                    </div>
                </Row>
                <Show when={profile.retired}>
                    <Row label='OAS received'>
                        <NumberBox value={profile.oasAnnual ?? 0} onValue={(n) => setOasAnnual(n === 0 ? null : n)} prefix='$' width='w-36' border='l' />
                    </Row>
                    <div class='border-b px-2 py-3'>
                        <Text tone='muted'>
                            {OAS_RECOVERY.threshold == null
                                ? `The ${TAX_YEAR} clawback threshold is not in shared/tax-rates yet, so nothing is computed from this. It goes in beside the marginal rates, which are replaced together once a year.`
                                : `Recovery is ${OAS_RECOVERY.rate}% of net income above ${formatCurrency(OAS_RECOVERY.threshold)}, capped at what you receive. Eligible dividends count grossed up by 38%, so a dividend book pushes net income higher than the cash it pays you.`}
                        </Text>
                    </div>
                </Show>

                <div class='px-2 py-3'>
                    <Show
                        when={assumption()}
                        fallback={
                            <Text tone='muted'>
                                Nothing shows an after-tax figure until both are set. A number computed against a blank profile is not a conservative estimate, it is a wrong one.
                            </Text>
                        }>
                        <Text tone='muted'>
                            After-tax figures state this beside them — {assumption()} — rather than leaving it here, because a page you visit once is a page you forget you set.
                        </Text>
                    </Show>
                </div>

                {/* What you can borrow, and on what terms.

                    Here rather than on the Loans page because a facility is a
                    fact about you, not about a book: you have one HELOC whether
                    you are looking at your real portfolio, the sandbox or a
                    plan, and it should not be three rows that can disagree about
                    the rate. The Loans page reads these and shows what they are
                    doing; this is where they are defined.

                    Wider than the fields above, so it escapes the max-w-2xl the
                    rest of the page sits in — six columns of a credit facility do
                    not fit a settings column. */}
                <div class='-mx-0 w-full max-w-none'>
                    <SectionLabel>What you can borrow</SectionLabel>
                    <LoanTable />
                </div>

                {/* Last, and the only control on this page that is not an input
                    to a calculation.

                    Here because there was nowhere else: signOut has existed in
                    the auth store since the beginning and nothing has ever called
                    it, so the only way out of the app was clearing site data.
                    The profile is the one screen that is already about the person
                    rather than the book, which makes it where you would look. */}
                <SectionLabel>Account</SectionLabel>
                <Row label={session()?.user?.email ?? 'Signed in'}>
                    {/* One click, no confirmation. Nothing is lost — the book is
                        in the database and signing back in returns to it — and a
                        confirm on a harmless action teaches people to click
                        through the ones that are not. */}
                    <Button size='fill' label='Sign out' title='Sign out of this account' class='w-36 border-l' onClick={() => void signOut()} />
                </Row>

                {/* Return of capital is the one every covered-call holder gets
                    wrong, and it is not a rate — so it is said here rather than
                    given a field. */}
                <div class='border-t px-2 py-3'>
                    <Text tone='muted'>
                        Return of capital is not taxed when you receive it. It lowers your cost base instead, so it is taxed later as a larger capital gain — and if the money was borrowed, spending it
                        rather than reinvesting it shrinks the share of your interest that stays deductible.
                    </Text>
                </div>
            </div>
        </div>
    </Panel>
);

export default Profile;
